'use client'

import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  Table as TanstackTable,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { DataTablePagination } from './data-table-pagination'
import { useEffect } from 'react'

interface DataTableProps<TData, TValue> {
  data: TData[]
  columns: ColumnDef<TData, TValue>[]
  toolbar?: (table: TanstackTable<TData>) => React.ReactNode
  renderRowAction?: (row: TData) => React.ReactNode
  rowCount?: number
  defaultPageSize: number
  emptyStateLabel?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  toolbar,
  defaultPageSize,
  rowCount,
  emptyStateLabel = 'No results.',
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  })

  // useEffect(() => {
  //   if (onChangePage) {
  //     onChangePage(pagination.pageIndex);
  //   }
  // }, [pagination.pageIndex]);

  // useEffect(() => {
  //   if (onPaginationChange) {
  //     console.log("pagination.pageSize", pagination);
  //     onPaginationChange(pagination);
  //   }
  // }, [pagination]);

  // useEffect(() => {
  //   if (onSortingChange) {
  //     onSortingChange(sorting);
  //   }
  // }, [sorting]);

  // useEffect(() => {
  //   if (onColumnFiltersChange) {
  //     onColumnFiltersChange(columnFilters);
  //   }
  // }, [columnFilters]);

  const table = useReactTable({
    data,
    columns,
    ...(rowCount !== undefined && rowCount > 0 && { rowCount }),
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination,
    },
    enableRowSelection: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  const pageCount = table.getPageCount()

  return (
    <div className="space-y-4">
      {toolbar && toolbar(table)}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={'w-4'}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => {
                const rowData = row.original as any
                const isLocationHeader = rowData?.type === 'location-header'

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={
                      isLocationHeader ? 'bg-blue-50 hover:bg-blue-100' : ''
                    }
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns?.length}
                  className="h-24 text-center"
                >
                  {emptyStateLabel}
                </TableCell>
              </TableRow>
            )}
          </TableBody>{' '}
        </Table>
      </div>
      {pageCount > 1 && <DataTablePagination table={table} />}
    </div>
  )
}
